import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { strongPasswordRegex } from '@core/constants';
import { AlertService } from '@core/services/alert/alert.service';
import {
  FinishChangePassword,
  InitChangePassword,
} from '@core/state/userProfile/userProfile.actions';
import { selectChangePasswordLoading } from '@core/state/userProfile/userProfile.selectors';
import { PasswordValidators } from '@core/validators/password-validators';
import { Store } from '@ngrx/store';
import {
  FormValidationMessageModule,
  PasswordRulesModule,
} from '@shipmercury/mercury-ui';
import { DividerModule } from 'primeng/divider';
import { PasswordModule } from 'primeng/password';
import { Observable } from 'rxjs';
import { CustomDialogComponent } from '../../shared/components/custom-dialog/custom-dialog.component';
import { ChangePasswordService } from './change-password.service';

@Component({
  selector: 'au-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  imports: [
    CustomDialogComponent,
    ReactiveFormsModule,
    PasswordModule,
    FormValidationMessageModule,
    DividerModule,
    PasswordRulesModule,
    AsyncPipe
],
})
export class ChangePasswordComponent {
  @Input() toggleChangePassword = false;
  @Output() toggleChangePasswordChange = new EventEmitter<any>();
  form!: FormGroup;
  isRequestInProgress$: Observable<boolean>;
  strongRegex = strongPasswordRegex;
  showPasswordErrors: boolean = false;

  constructor(
    private changePasswordService: ChangePasswordService,
    private store: Store<any>,
    private alertService: AlertService,
    fb: FormBuilder
  ) {
    this.form = fb.group(
      {
        currentPassword: [null, [Validators.required]],
        password: [
          null,
          [
            Validators.required,
            PasswordValidators.containsUpperCaseCharacter,
            PasswordValidators.containsLowerCaseCharacter,
            PasswordValidators.containsSpeicalCharacter,
            PasswordValidators.containsNumericCharacter,
            Validators.minLength(8),
          ],
        ],
        passwordRepeat: [
          null,
          {
            validators: [Validators.required],
          },
        ],
      },
      {
        validators: [PasswordValidators.passwordsMatch()],
        updateOn: 'change',
      }
    );
    this.isRequestInProgress$ = store.select(selectChangePasswordLoading);
  }

  get currentPassword(): FormControl {
    return this.form.get('currentPassword') as FormControl;
  }

  get password(): FormControl {
    return this.form.get('password') as FormControl;
  }

  get passwordRepeat(): FormControl {
    return this.form.get('passwordRepeat') as FormControl;
  }

  async sendChangePasswordRequest() {
    this.checkFormValidation();

    if (!this.form.valid) return;

    this.store.dispatch(InitChangePassword());

    this.changePasswordService
      .sendChangePasswordRequest(
        this.currentPassword.value,
        this.password.value,
        this.passwordRepeat.value
      )
      .subscribe((res) => {
        if (res && res.success) {
          this.alertService.success({
            message: 'You have changed your password successfully. ',
            title: 'Success',
          });
          this.clearForm();
        }
        this.store.dispatch(FinishChangePassword());
      });
  }

  cancelChangePassword = () => {
    this.toggleChangePasswordChange.emit();
  };

  private clearForm = () => {
    this.toggleChangePasswordChange.emit();
    this.form.reset();
  };

  private checkFormValidation = () => {
    this.form.get('currentPassword')?.markAsDirty();
    this.form.get('password')?.markAsDirty();
    this.form.get('passwordRepeat')?.markAsDirty();
  };

  handleFocusBlurEvents(event: any) {
    switch (event.type) {
      case 'focus':
        this.showPasswordErrors = false;
        break;
      case 'blur':
        this.showPasswordErrors = true;
        break;
      default:
        this.showPasswordErrors = false;
    }
  }
}
